import { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { BoxTruckPricingEquipment, BoxTruckPricingQuote, BoxTruckPricingRequest } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { useAppSelector } from 'hooks'
import { loadKey, vehicleLabel } from '../utils/loadFields'
import { fetchBoxTruckPricingQuote } from '../services/boxTruckLoadsApi'
import { trackBoxTruckPricingQuote } from '../utils/analytics'
import { log } from 'utils/logger'

/**
 * Which learned model answers for a board row — or none.
 *
 * Read off the VEHICLE LABEL the board already shows, which is the only field that distinguishes the
 * classes the tenant actually dispatches: the canonical equipment code collapses cargo van and sprinter
 * into one and both straight sizes into another, while the history is tagged exactly those two ways.
 *
 * Dry Van is deliberately unpriced. It is on this board as a supplement, it is tractor-size freight, and
 * pricing it off a box-truck curve would produce a confident number for a load the curve has never seen.
 */
const pricingEquipmentFor = (load: CanonicalLoad): BoxTruckPricingEquipment | null => {
    const label = vehicleLabel(load)
    switch (label) {
        case 'Cargo Van':
        case 'Sprinter':
            return 'CARGO_VAN'
        case 'Small Straight':
        case 'Large Straight':
            return 'BOX_TRUCK'
        // Sylectus vehicle sizes are free text and the board folds only the five it knows; the rest arrive
        // title-cased as posted. `DOCK HIGH STRAIGHT` is a straight truck whatever the fold does with it,
        // and refusing to price it is a precision nobody asked for. `Dry Van` is matched by name above —
        // it is tractor freight and the only other label carrying "van".
        default:
            return /straight/i.test(label) ? 'BOX_TRUCK' : null
    }
}

/**
 * Postgres `numeric` reaches the client as a STRING, while `CanonicalLoad` types these fields as `number`.
 *
 * EVERY numeric field goes through this, coordinates included. The loadboard happens to normalize its four
 * coordinates at the SSE boundary; the Bidding page does not — `fetchBoxTruckBids` returns them as
 * `'33.43'` — so a load opened from there passed every truthiness check here and was then refused by the
 * server's own `typeof === 'number'` with a 400, which the panel showed as "could not price this load".
 * Trusting the declared type instead of the value is what made both this and the posted-rate defect
 * invisible until the live board was run through them.
 */
const finite = (value: unknown): number | undefined => {
    const parsed = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN
    return Number.isFinite(parsed) ? parsed : undefined
}

const minutesSince = (value: Date | string | null | undefined): number | undefined => {
    if (!value) return undefined
    const posted = value instanceof Date ? value.getTime() : Date.parse(value)
    if (!Number.isFinite(posted)) return undefined
    return Math.max(0, Math.round((Date.now() - posted) / 60_000))
}

/** What to do with this row: price it, say why it cannot be priced, or show nothing. */
type Plan = { kind: 'price'; request: BoxTruckPricingRequest } | { kind: 'unpriceable'; reason: string } | { kind: 'none' }

const planFor = (load: CanonicalLoad | null): Plan => {
    if (!load) return { kind: 'none' }
    const equipment = pricingEquipmentFor(load)
    if (!equipment) return { kind: 'unpriceable', reason: `${vehicleLabel(load)} loads are not priced yet.` }
    const miles = finite(load.changedMiles) ?? finite(load.distanceMiles)
    const [lat, lng, dLat, dLng] = [finite(load.pickupLat), finite(load.pickupLon), finite(load.deliveryLat), finite(load.deliveryLon)]
    if (!miles || miles <= 0 || lat === undefined || lng === undefined || dLat === undefined || dLng === undefined) {
        return { kind: 'unpriceable', reason: 'This post has no miles or coordinates to price from.' }
    }
    return {
        kind: 'price',
        request: {
            equipment,
            origin: { lat, lng, state: load.pickupStateProv ?? undefined },
            destination: { lat: dLat, lng: dLng, state: load.deliveryStateProv ?? undefined },
            loadedMiles: miles,
            postedRate: finite(load.rateAmountUsd),
            brokerName: load.brokerCompanyName ?? undefined,
            // On this board `createdAt` IS the broker's posted time (the ingest never overwrites it).
            postedAgeMinutes: minutesSince(load.createdAt),
        },
    }
}

/**
 * Everything about this load that can change the answer, as one string.
 *
 * The board is live: `selectedLoad` is re-resolved with `visibleLoads.find(...)` on every refetch and
 * realtime push, so keying on the object would re-POST a quote the whole time the drawer sat open. Keying
 * on the load's IDENTITY alone went too far the other way — a broker reposting from $1,300 to $1,600 left
 * the panel anchored on the old rate while the recorded decision read the new one off the same object, so
 * the stored suggestion and posted rate came from two different versions of the load. This keys on the
 * identity AND the fields the quote is computed from, which is the only pair of properties that holds.
 */
const priceKey = (load: CanonicalLoad | null): string => {
    if (!load) return ''
    return [
        loadKey(load),
        vehicleLabel(load),
        finite(load.changedMiles) ?? finite(load.distanceMiles) ?? '',
        finite(load.rateAmountUsd) ?? '',
        finite(load.pickupLat) ?? '',
        finite(load.pickupLon) ?? '',
        finite(load.deliveryLat) ?? '',
        finite(load.deliveryLon) ?? '',
        load.brokerCompanyName ?? '',
        // The state codes are a quote input, not decoration: they carry the state-pair and origin-state
        // priors when the lane itself has no nearby loads. A push that fills them in on a load first seen
        // without them has to re-price, or the panel keeps sending `state: undefined` while showing them.
        load.pickupStateProv ?? '',
        load.deliveryStateProv ?? '',
    ].join('|')
}

export type BoxTruckPricingState =
    | { status: 'hidden' }
    | { status: 'unpriceable'; reason: string }
    | { status: 'loading' }
    /** `request` rides along so the bid record can post the INPUTS and let the server re-price them. */
    | { status: 'ready'; quote: BoxTruckPricingQuote; request: BoxTruckPricingRequest }
    | { status: 'no-model' }
    | { status: 'error' }

/**
 * The pricing panel's data, for the load the drawer currently has open.
 *
 * Re-priced only when something that changes the answer changes — which is also what keeps the analytics
 * event to one per priced load rather than one per realtime push. The pilot has no live model, so the
 * model itself cannot move under the dispatcher; the load can, and that is what `priceKey` watches.
 */
export function useBoxTruckPricing(load: CanonicalLoad | null): BoxTruckPricingState {
    const allowed = useAppSelector((state) => state.boxTruckLoads.pricing)
    const key = priceKey(load)
    // `postedAgeMinutes` is the one input not in the key: it is derived from `Date.now()`, so it moves
    // without the load changing and no key could track it. It does not change the chosen pair either.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` covers every LOAD field `planFor` reads.
    const plan = useMemo(() => planFor(load), [key])
    const [state, setState] = useState<BoxTruckPricingState>({ status: 'hidden' })

    useEffect(() => {
        if (!allowed || plan.kind === 'none') {
            setState({ status: 'hidden' })
            return
        }
        if (plan.kind === 'unpriceable') {
            setState({ status: 'unpriceable', reason: plan.reason })
            trackBoxTruckPricingQuote('unpriceable')
            return
        }
        const controller = new AbortController()
        setState({ status: 'loading' })
        fetchBoxTruckPricingQuote(plan.request, controller.signal)
            .then((quote) => {
                if (controller.signal.aborted) return
                setState(quote ? { status: 'ready', quote, request: plan.request } : { status: 'no-model' })
                trackBoxTruckPricingQuote(quote ? 'ready' : 'no-model', plan.request.equipment, quote?.confidence)
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted || axios.isCancel(error)) return
                log.w('[box-truck-pricing] quote failed', { error: error instanceof Error ? error.message : String(error) })
                setState({ status: 'error' })
                trackBoxTruckPricingQuote('error', plan.request.equipment)
            })
        return () => controller.abort()
    }, [allowed, plan])

    return state
}
